import { Switch, Route } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { useEffect, useState, lazy, Suspense } from "react";
import { GrowthBookProvider } from "@growthbook/growthbook-react";
import { GrowthBook } from "@growthbook/growthbook";
import { HelmetProvider } from "react-helmet-async";
import {
  autoAttributesPlugin,
  growthbookTrackingPlugin,
} from "@growthbook/growthbook/plugins";
import { captureTrackingParams } from "@/lib/tracking";
import { Loader2 } from "lucide-react";
import ErrorBoundary from "@/components/ErrorBoundary";
import posthog from "@/lib/posthog";
import { ContentProvider } from "@/contexts/ContentContext";

// Lazy load: All pages including home variants
const HomePageControl = lazy(() => import("@/pages/home/control"));
const HomePageVariantA = lazy(() => import("@/pages/home/variant-a"));
const HomePageVariantB = lazy(() => import("@/pages/home/variant-b"));
const HomePageVariantC = lazy(() => import("@/pages/home/variant-c"));
const HomePageVariantD = lazy(() => import("@/pages/home/variant-d"));
const FullCourse = lazy(() => import("@/pages/full-course"));
const Success = lazy(() => import("@/pages/success"));
const Program = lazy(() => import("@/pages/program"));
const TestStorage = lazy(() => import("@/pages/test-storage"));
const QuickCheckout = lazy(() => import("@/pages/buy"));
const Admin = lazy(() => import("@/pages/admin"));
const FAQ = lazy(() => import("@/pages/faq"));
const Curriculum = lazy(() => import("@/pages/curriculum"));
const NotFound = lazy(() => import("@/pages/not-found"));

// Initialize GrowthBook instance
const growthbook = new GrowthBook({
  apiHost: "https://cdn.growthbook.io",
  clientKey: "sdk-Jy4A9UJrH0Zt4bxY",
  enableDevMode: true,
  plugins: [autoAttributesPlugin(), growthbookTrackingPlugin()],
});

// Expose GrowthBook to window for debugging
if (typeof window !== "undefined") {
  (window as any).growthbook = growthbook;
}

// Debug GrowthBook connection
if (process.env.NODE_ENV === "development") {
  growthbook.setRenderer(() => {
    // Log feature flag changes in development
    console.log("[GB] Features loaded:", Object.keys(growthbook.getFeatures()));
  });
}

// Map variant strings to components
const HomePageVariants = {
  control: HomePageControl,
  variant_a: HomePageVariantA,
  variant_b: HomePageVariantB,
  variant_c: HomePageVariantC,
  variant_d: HomePageVariantD,
} as const;

// Valid variant values
const VALID_VARIANTS = [
  "control",
  "variant_a",
  "variant_b",
  "variant_c",
  "variant_d",
] as const;
type ValidVariant = (typeof VALID_VARIANTS)[number];

function isValidVariant(value: unknown): value is ValidVariant {
  return (
    typeof value === "string" &&
    (VALID_VARIANTS as readonly string[]).includes(value)
  );
}

// Landing Page Variants Router - PostHog handles stickiness
function HomePageVariantsRouter() {
  const [assignedVariant, setAssignedVariant] = useState<string | null>(null);
  const [posthogReady, setPosthogReady] = useState(false);

  // Wait for PostHog feature flags to load
  useEffect(() => {
    const checkFlags = () => {
      const flag = posthog.getFeatureFlag("landing-page-variant");
      if (flag !== undefined) {
        setPosthogReady(true);
        return true;
      }
      return false;
    };

    if (checkFlags()) return;

    posthog.onFeatureFlags(() => {
      setPosthogReady(true);
      if (process.env.NODE_ENV === "development") {
        console.log("[PostHog] Feature flags loaded");
      }
    });

    // Fallback timeout - don't block forever if PostHog fails
    const timeout = setTimeout(() => {
      if (!posthogReady) {
        console.warn("[PostHog] Feature flags timeout, defaulting to control");
        setPosthogReady(true);
      }
    }, 3000);

    return () => clearTimeout(timeout);
  }, []);

  // Assign variant once PostHog is ready
  useEffect(() => {
    if (!posthogReady) return;

    // 1. Check for QA force override (always takes priority)
    const urlParams = new URLSearchParams(window.location.search);
    const forceParam = urlParams.get("force");

    let variant: string;
    let source: "force" | "posthog" | "default";

    if (isValidVariant(forceParam)) {
      variant = forceParam;
      source = "force";
    } else {
      // 2. Get from PostHog (handles stickiness automatically via distinct_id)
      const posthogVariant = posthog.getFeatureFlag("landing-page-variant");
      if (isValidVariant(posthogVariant)) {
        variant = posthogVariant;
        source = "posthog";
      } else {
        // 3. Default to control
        variant = "control";
        source = "default";
      }
    }

    setAssignedVariant(variant);

    // Track assignment
    try {
      posthog.capture("landingpage_variant_assigned", {
        experiment_name: "landing_page_variants",
        variant,
        source,
        entry_url: window.location.href,
        referrer: document.referrer || "direct",
      });
    } catch (error) {
      console.warn("[PostHog] Failed to track variant assignment:", error);
    }

    if (process.env.NODE_ENV === "development") {
      console.log(`[LandingPage] Variant: ${variant} (source: ${source})`);
    }
  }, [posthogReady]);

  // Show loading until variant is assigned
  if (!assignedVariant) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin mx-auto mb-4 text-pink-600" />
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  const VariantComponent =
    HomePageVariants[assignedVariant as keyof typeof HomePageVariants] ||
    HomePageControl;

  return <VariantComponent />;
}

// Loading component for lazy-loaded routes
function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <Loader2 className="h-12 w-12 animate-spin mx-auto mb-4 text-gray-600" />
        <p className="text-gray-600">Loading...</p>
      </div>
    </div>
  );
}

function Router() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Switch>
        <Route path="/" component={HomePageVariantsRouter} />
        <Route path="/full-course" component={FullCourse} />
        <Route path="/buy" component={QuickCheckout} />
        <Route path="/success" component={Success} />
        <Route path="/program" component={Program} />
        <Route path="/faq" component={FAQ} />
        <Route path="/curriculum" component={Curriculum} />
        <Route path="/test-storage" component={TestStorage} />
        <Route path="/admin" component={Admin} />
        <Route component={NotFound} />
      </Switch>
    </Suspense>
  );
}

function App() {
  useEffect(() => {
    // Load features asynchronously when the app renders
    growthbook.init({ streaming: true });

    // Capture tracking parameters from URL and cookies
    captureTrackingParams();
  }, []);

  return (
    <HelmetProvider>
      <GrowthBookProvider growthbook={growthbook}>
        <QueryClientProvider client={queryClient}>
          <ContentProvider>
            <ErrorBoundary>
              <TooltipProvider>
                <Toaster />
                <Router />
              </TooltipProvider>
            </ErrorBoundary>
          </ContentProvider>
        </QueryClientProvider>
      </GrowthBookProvider>
    </HelmetProvider>
  );
}

export default App;
